<template>
  <div>
    <v-carousel
      v-if="!printView"
      height="400"
      :model-value="carouselModel"
      :hide-delimiters="true"
      class="keyword-row__carousel mb-6 mb-sm-4"
    >
      <p class="keyword-row__carousel-pagination">{{ carouselModel + 1 }}/{{ keywords.length }}</p>
      <template #prev>
        <v-btn
          variant="outlined"
          class="prev"
          color="secondary"
          :disabled="carouselModel === 0"
          :style="{ marginRight: `${prevBtnPosition}px` }"
          @click="handlePrev"
        >
          <FontAwesomeIcon class="mr-2" size="sm" icon="fa-solid fa-chevron-left" />
          {{ smAndUp ? t(l.flash_card.prev_slide) : t(l.flash_card.prev_slide_short) }}
        </v-btn>
      </template>
      <template #next>
        <v-btn
          ref="nextBtn"
          class="next mr-2"
          variant="outlined"
          color="secondary"
          :disabled="carouselModel + 1 === keywords.length"
          @click="handleNext"
        >
          {{ smAndUp ? t(l.flash_card.next_slide) : t(l.flash_card.next_slide_short) }}
          <FontAwesomeIcon class="ml-2" size="sm" icon="fa-solid fa-chevron-right" />
        </v-btn>
      </template>
      <v-carousel-item v-for="keyword of keywords" :key="keyword.title">
        <v-row
          no-gutters
          :class="['ma-2', 'keyword-row__flip-cards', { flipped: cardFlipped || false }]"
          align="stretch"
          @click="flipCard()"
        >
          <!-- Flash card front -->
          <v-col class="d-flex keyword-row__flip-cards-front justify-center align-center flex-column px-10" cols="12">
            <div class="d-flex align-center">
              <h3 class="bold mr-2 card-name">{{ translateKeyword(keyword, selectedNative)?.title }}</h3>
              <PlayNarrationButton
                v-if="
                  translateKeyword(keyword, selectedNative)?.title &&
                  getKeywordUrl(translateKeyword(keyword, selectedNative)?.title, selectedNative) &&
                  selectedNative === 'sv' &&
                  !printView
                "
                class="ml-2"
                :value="getKeywordUrl(translateKeyword(keyword, selectedNative)?.title, selectedNative)"
              />
            </div>
            <hr v-if="translateKeyword(keyword, selectedTranslation)?.title" class="my-5" />
            <div v-if="translateKeyword(keyword, selectedTranslation)?.title" class="d-flex align-center">
              <h3 class="bold mr-2 card-name">{{ translateKeyword(keyword, selectedTranslation)?.title }}</h3>
              <PlayNarrationButton
                v-if="
                  translateKeyword(keyword, selectedTranslation) &&
                  getKeywordUrl(translateKeyword(keyword, selectedTranslation)?.title, selectedTranslation) &&
                  !printView
                "
                class="ml-2"
                :value="
                  translateKeyword(keyword, selectedTranslation) &&
                  getKeywordUrl(translateKeyword(keyword, selectedTranslation)?.title, selectedTranslation)
                "
              />
            </div>
          </v-col>
          <!-- Flash card back -->
          <v-col class="d-flex keyword-row__flip-cards-back justify-center align-center px-10" cols="12">
            <div class="pr-7 relative" :class="{ 'mr-12': !!keyword.imageId }">
              <p class="mb-0" :class="selectedNative">
                {{ translateKeyword(keyword, selectedNative)?.description }}
                <PlayNarrationButton
                  v-if="
                    getKeywordUrl(translateKeyword(keyword, selectedNative)?.description, selectedNative) &&
                    selectedNative === 'sv' &&
                    !printView
                  "
                  class="ml-2 d-inline-block"
                  :value="getKeywordUrl(translateKeyword(keyword, selectedNative)?.description, selectedNative)"
                />
              </p>
              <hr v-if="translateKeyword(keyword, selectedTranslation)" class="my-5" />
              <p class="mb-0" :class="selectedTranslation">
                {{ translateKeyword(keyword, selectedTranslation)?.description }}
                <PlayNarrationButton
                  v-if="
                    translateKeyword(keyword, selectedTranslation)?.description &&
                    getKeywordUrl(translateKeyword(keyword, selectedTranslation)?.description, selectedTranslation) &&
                    !printView
                  "
                  class="ml-2 d-inline-block"
                  :value="
                    getKeywordUrl(translateKeyword(keyword, selectedTranslation)?.description, selectedTranslation)
                  "
                />
              </p>
            </div>
            <v-img
              v-if="keyword.imageId"
              :src="getKeywordImageUrl(keyword)"
              :alt="altTexts ? altTexts[keyword.imageId] : ''"
              cover
              class="ml-4"
              width="30%"
              max-width="30%"
            />
          </v-col>
          <p class="keyword-row__flip-cards-hint">{{ t(l.flash_card.click_to_flip) }}</p>
        </v-row>
      </v-carousel-item>
    </v-carousel>
    <v-row
      v-for="(keyword, idx) in keywords"
      v-else
      :key="idx"
      no-gutters
      :class="['keyword-row__flash-cards', { 'no-explanation': !isExplanationVisible }]"
      align="stretch"
    >
      <!--native keyword-->
      <v-col class="d-flex keyword-wrap" cols="6">
        <v-card
          class="pa-6 d-flex flex-grow-1 align-center text-center keyword-item justify-space-between"
          rounded="0"
          :dir="isRTL(selectedNative) ? 'rtl' : 'ltr'"
        >
          <p class="bold">
            <PlayNarrationButton
              v-if="
                getKeywordUrl(translateKeyword(keyword, selectedNative)?.title, selectedNative) &&
                getKeywordUrl(translateKeyword(keyword, selectedNative)?.title, selectedNative)?.[0]?.url
              "
              class="ml-2"
              :value="getKeywordUrl(translateKeyword(keyword, selectedNative)?.title, selectedNative)"
            />
            {{ translateKeyword(keyword, selectedNative)?.title }}
          </p>
          <img
            :src="getKeywordImageUrl(keyword, 116, 116)"
            alt=""
            height="116"
            class="ml-4 pointer"
            style="background-size: cover"
          />
        </v-card>
      </v-col>
      <!--native keyword explanation-->
      <v-col v-if="isExplanationVisible" class="d-flex keyword-wrap" cols="6">
        <v-card
          class="pa-6 d-flex flex-grow-1 align-center keyword-item keyword-item__right"
          rounded="0"
          :dir="isRTL(selectedNative) ? 'rtl' : 'ltr'"
        >
          <p>
            {{ translateKeyword(keyword, selectedNative)?.description }}
          </p>
        </v-card>
      </v-col>
      <!--translation keyword-->
      <v-col v-if="translateKeyword(keyword, selectedTranslation)?.title" class="d-flex keyword-wrap" cols="6">
        <v-card
          class="pa-6 d-flex flex-grow-1 align-center text-center keyword-item justify-space-between"
          rounded="0"
          :dir="isRTL(selectedTranslation) ? 'rtl' : 'ltr'"
        >
          <p class="bold">
            <PlayNarrationButton
              v-if="
                getKeywordUrl(translateKeyword(keyword, selectedNative)?.title, selectedNative) &&
                getKeywordUrl(translateKeyword(keyword, selectedNative)?.title, selectedNative)?.[0]?.url
              "
              class="ml-2"
              :value="getKeywordUrl(translateKeyword(keyword, selectedNative)?.title, selectedNative)"
            />
            {{ translateKeyword(keyword, selectedTranslation)?.title }}
          </p>
          <img
            :src="getKeywordImageUrl(keyword, 116, 116)"
            alt=""
            height="116"
            class="ml-4 pointer"
            style="background-size: cover"
          />
        </v-card>
      </v-col>
      <!--translation explanation-->
      <v-col
        v-if="translateKeyword(keyword, selectedTranslation)?.description && isExplanationVisible"
        class="d-flex keyword-wrap"
        cols="6"
      >
        <v-card
          v-if="isExplanationVisible"
          class="pa-6 d-flex flex-grow-1 align-center keyword-item keyword-item__right"
          rounded="0"
          :dir="isRTL(selectedTranslation) ? 'rtl' : 'ltr'"
        >
          <p>
            {{ translateKeyword(keyword, selectedTranslation)?.description }}
          </p>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script setup lang="ts">
import { stopAllSounds } from '@ilteducation/audio-resources';
import { computed, nextTick, ref, watch } from 'vue';
import { useLocale, useLocalisation } from '@/plugins/translation';
import useKeywordsStore, { KeywordWithTranslations } from '@/store/keywords';
import type { AudioResource } from '@ilteducation/videos';
import isRTL from '@/composables/use-is-rtl-language';
import { storeToRefs } from 'pinia';
import useAudioStore from '@/store/audio';
import PlayNarrationButton from '../../base/PlayNarrationButton.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useDisplay } from 'vuetify';

type Props = {
  selectedNative: string;
  selectedTranslation: string;
  keywords: KeywordWithTranslations[];
  altTexts: Record<string, string>;
  isExplanationVisible: boolean;
  printView?: boolean;
};

withDefaults(defineProps<Props>(), {
  selectedTranslation: '',
});
const { getKeywordImageUrl, translateKeyword } = useKeywordsStore();
const { audioResources } = storeToRefs(useAudioStore());
const { t, l } = useLocale();
const { smAndUp } = useDisplay();

const carouselModel = ref(0);

const getKeywordUrl = computed(() => (kw: string | undefined, lang: string): AudioResource[] | null => {
  if (!kw) {
    return null;
  }
  const audio = audioResources.value.find((resource: AudioResource) => resource.key === kw && resource.lang === lang);
  return audio ? [audio] : null;
});

const cardFlipped = ref(false);
const nextBtn = ref();
const prevBtnPosition = ref(0);

const { language: currentLanguage } = useLocalisation();

watch(
  currentLanguage,
  () => {
    nextTick(() => {
      if (nextBtn.value) {
        prevBtnPosition.value = nextBtn.value.$el.clientWidth + 26;
      }
    });
  },
  { immediate: true },
);

const flipCard = () => {
  stopAllSounds();
  cardFlipped.value = !cardFlipped.value;
};

const handleNext = () => {
  cardFlipped.value = false;
  carouselModel.value += 1;
};

const handlePrev = () => {
  cardFlipped.value = false;
  carouselModel.value -= 1;
};
</script>

<style scoped lang="scss">
@use '@/foundations/theme/settings';

.v-sheet--outlined {
  border-color: rgba(0, 0, 0, 0.7);
}

// from _content.scss
// TODO: mobile styling
.keyword-row {
  padding: rem-calc(10 0);
  margin: 0 !important;
  justify-content: space-between;

  //@include breakpoint(large up) {
  //  padding: rem-calc(6 0);
  //}
  .bold {
    margin-bottom: 0;
  }
  &__carousel {
    @media screen and (max-width: 767px) {
    }
    &.v-carousel {
      overflow: visible;
    }
    .v-carousel__item {
      overflow: visible;
    }
    .prev {
      position: absolute;
      top: unset;
      bottom: 4px;
      left: unset;
      right: 0;
      @media screen and (max-width: 767px) {
        left: 0;
        right: unset;
        transform: none;
        bottom: 0;
      }
    }
    .next {
      position: absolute;
      top: unset;
      right: 0;
      bottom: 4px;
      @media screen and (max-width: 767px) {
        bottom: 0;
      }
    }
    &-pagination {
      position: absolute;
      bottom: 8px;
      left: 16px;
      color: #596a79;
      line-height: 24px;
      @media screen and (max-width: 767px) {
        position: relative;
        bottom: 0;
        left: 0;
      }
    }
  }
  .keyword-item {
    border-bottom: 1px solid #d6dbde;
    padding-bottom: 15px;
    &__right {
      padding-bottom: 20px;
    }

    //p {
    //  @include breakpoint(large up) {
    //    font-size: 1.08rem;
    //  }
    //}

    .ar,
    .prs,
    .fa {
      direction: rtl !important;
    }
  }

  &__flash-cards {
    margin-bottom: 32px !important;
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    break-inside: avoid;
    .keyword-wrap {
      border-bottom: 1px solid #d6dbde;
      //last two items
      &:nth-last-of-type(-n + 2) {
        border-bottom: none;
      }
    }
    .keyword-item {
      box-shadow: none !important;
      &__right {
        border-left: 1px solid #d6dbde;
      }
    }
    &.no-explanation {
      box-shadow: none;
      .keyword-wrap {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        &:nth-of-type(2) {
          border-left: 1px solid #d6dbde;
        }
      }
    }
  }
  &__flip-cards {
    position: relative;
    height: 300px;
    margin-bottom: 32px !important;
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transition: transform 0.8s;
    transform-style: preserve-3d;
    perspective: 1000px;
    font-size: 1rem;
    color: #122b40;
    text-align: center;
    cursor: pointer;
    background-color: rgb(var(--v-theme-surface-container-lowest));

    &:hover {
      .keyword-row__flip-cards-hint {
        color: #005eb8;
      }
    }
    hr {
      display: block;
      width: 100%;
      height: 1px;
      background: #d6dbde;
      border: none;
    }
    .card-name {
      font-size: 1.75rem;
      line-height: 2.75rem;
      margin-top: 0;
      margin-bottom: 0;
    }
    &-front,
    &-back {
      position: absolute;
      width: 100%;
      height: 100%;
      backface-visibility: hidden;
      border: 1px solid transparent;
      transition: all 0.3s ease;
      &:hover {
        border-color: #005eb8;
      }
    }
    &-back {
      transform: rotateX(180deg);
      p {
        text-align: left;
        justify-content: center;
      }
      .arb,
      .ar,
      .prs,
      .fas,
      .fa {
        direction: rtl;
        text-align: right;
      }
    }
    &.flipped {
      transform: rotateX(180deg);
      .keyword-row__flip-cards-front {
        visibility: hidden;
      }
      .keyword-row__flip-cards-hint {
        transform: rotateX(180deg) translate(-50%, -100%);
        bottom: calc(100% - 4px);
      }
    }
    &-hint {
      position: absolute;
      bottom: 4px;
      font-size: 0.75rem;
      line-height: 1.5;
      color: #aeb7be;
      left: 50%;
      transform: translate(-50%, 0);
      transition: all 0.3s ease;
    }
    .keyword-wrap {
      border-bottom: 1px solid #d6dbde;
      //last two items
      &:nth-last-of-type(-n + 2) {
        border-bottom: none;
      }
    }
    .keyword-item {
      box-shadow: none !important;
      &__right {
        border-left: 1px solid #d6dbde;
      }
    }
    &.no-explanation {
      box-shadow: none;
      .keyword-wrap {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        &:nth-of-type(2) {
          border-left: 1px solid #d6dbde;
        }
      }
    }
  }
}
</style>
